import { Link, router } from "@inertiajs/react";
import { CheckCircle, Download, ExternalLink, Mail, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Candidate, PackageData, csrfToken } from "./_types";

interface Props {
  pkg: PackageData;
  candidate: Candidate;
}

function UploadZone({ label, filename, url, variant, candidateId }: {
  label: string;
  filename: string | null;
  url: string | null;
  variant: "original" | "optimized";
  candidateId: number;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("variant", variant);
    fd.append("authenticity_token", csrfToken());
    fetch(`/candidates/${candidateId}/package/upload_resume`, { method: "POST", body: fd })
      .then(() => router.reload())
      .finally(() => setUploading(false));
  }

  return (
    <div
      className="border border-dashed border-gray-200 rounded-lg p-4 text-center cursor-pointer hover:border-green-400 hover:bg-green-50/30 transition-colors"
      onClick={() => inputRef.current?.click()}
    >
      <input ref={inputRef} type="file" accept=".pdf,.docx" className="hidden" onChange={handleFile} />
      {filename ? (
        <div>
          <p className="text-xs font-medium text-green-700 flex items-center justify-center gap-1">
            <CheckCircle className="w-3 h-3" /> {filename}
          </p>
          {url && (
            <a href={url} target="_blank" rel="noreferrer"
              className="text-xs text-blue-600 hover:underline mt-1 inline-flex items-center gap-0.5"
              onClick={(e) => e.stopPropagation()}>
              <Download className="w-3 h-3" /> View
            </a>
          )}
          <p className="text-xs text-gray-400 mt-1">Click to replace</p>
        </div>
      ) : (
        <div>
          <Upload className="w-5 h-5 text-gray-300 mx-auto mb-1" />
          <p className="text-xs font-medium text-gray-600">{label}</p>
          <p className="text-xs text-gray-400">PDF or Word</p>
        </div>
      )}
      {uploading && <p className="text-xs text-gray-400 mt-1">Uploading...</p>}
    </div>
  );
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "1 day ago";
  return `${days} days ago`;
}

export default function ResumeSection({ pkg, candidate }: Props) {
  const reviewed = pkg.resume_status === "approved" || pkg.resume_status === "skipped";

  function markReviewed() {
    router.patch(`/candidates/${candidate.id}/package`, {
      package: { resume_status: "approved" },
    });
  }

  return (
    <Card className={reviewed ? "border-green-200" : ""}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            {reviewed && <CheckCircle className="w-4 h-4 text-green-500" />}
            Resume
          </CardTitle>
          <div className="flex items-center gap-2">
            <Link href={`/candidates/${candidate.id}#resume`} className="text-xs text-blue-600 hover:underline inline-flex items-center gap-0.5">
              Open full profile <ExternalLink className="w-3 h-3" />
            </Link>
            {!reviewed && (
              <Button variant="outline" size="sm" onClick={markReviewed}>Mark Reviewed</Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <UploadZone
          label="Original resume"
          filename={pkg.resume_original_filename}
          url={pkg.resume_original_url}
          variant="original"
          candidateId={candidate.id}
        />
        <UploadZone
          label="Optimized resume"
          filename={pkg.resume_optimized_filename}
          url={pkg.resume_optimized_url}
          variant="optimized"
          candidateId={candidate.id}
        />
        {pkg.last_resume_sent_at && (
          <p className="text-xs text-gray-400 flex items-center gap-1">
            <Mail className="w-3 h-3" /> Resume sent {relativeTime(pkg.last_resume_sent_at)}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
